import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import {
  type SessionRow,
  useLazyListSessionsQuery,
  useListSessionsQuery,
} from '../../members/api/sessionApi';

interface AdminSessionHistoryProps {
  memberId: string;
  coachId?: string;
}

interface SessionHistoryState {
  queryKey: string;
  sessions: SessionRow[];
  nextCursor: string | null;
}

const PAGE_SIZE = 25;

function mergeSessions(existing: SessionRow[], incoming: SessionRow[]): SessionRow[] {
  const byId = new Map(existing.map((session) => [session.session_id, session]));
  for (const session of incoming) byId.set(session.session_id, session);
  return [...byId.values()];
}

export default function AdminSessionHistory({ memberId, coachId }: AdminSessionHistoryProps) {
  const params = {
    limit: PAGE_SIZE,
    person_id: memberId,
    coach_person_id: coachId,
    role: 'coachee',
    include_unresolved: true,
    past_only: true,
  };
  const { currentData, isLoading, error } = useListSessionsQuery(params);
  const [loadMore, { isFetching }] = useLazyListSessionsQuery();
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const queryKey = `${memberId}:${coachId ?? ''}`;
  const [history, setHistory] = useState<SessionHistoryState>({
    queryKey,
    sessions: [],
    nextCursor: null,
  });
  const activeQueryKeyRef = useRef(queryKey);
  const loadingMoreRef = useRef(false);
  activeQueryKeyRef.current = queryKey;

  const sessions = history.queryKey === queryKey ? history.sessions : [];
  const nextCursor = history.queryKey === queryKey ? history.nextCursor : null;

  useEffect(() => {
    loadingMoreRef.current = false;
    setHistory({ queryKey, sessions: [], nextCursor: null });
    setLoadMoreFailed(false);
  }, [queryKey]);

  useEffect(() => {
    setHistory({
      queryKey,
      sessions: currentData?.items ?? [],
      nextCursor: currentData?.next_cursor ?? null,
    });
  }, [currentData, queryKey]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadMoreFailed(false);
    const requestedQueryKey = queryKey;
    try {
      const page = await loadMore({ ...params, cursor: nextCursor }).unwrap();
      if (activeQueryKeyRef.current !== requestedQueryKey) return;
      setHistory((current) =>
        current.queryKey === requestedQueryKey
          ? {
              queryKey: requestedQueryKey,
              sessions: mergeSessions(current.sessions, page.items),
              nextCursor: page.next_cursor ?? null,
            }
          : current
      );
    } catch {
      if (activeQueryKeyRef.current === requestedQueryKey) setLoadMoreFailed(true);
    } finally {
      loadingMoreRef.current = false;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return <Typography color="error">Failed to load Session history.</Typography>;
  }

  return (
    <Paper sx={{ mb: 3 }}>
      {sessions.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No Sessions found.</Typography>
        </Box>
      ) : (
        <List>
          {sessions.map((session, index) => (
            <Box key={session.session_id}>
              {index > 0 && <Divider />}
              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={session.session_type?.replace(/_/g, ' ') || 'Session'}
                  secondary={format(new Date(session.planned_start_at), 'MMM d, yyyy h:mm a')}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {(session.attachments?.length ?? 0) > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${session.attachments?.length} attachment${session.attachments?.length === 1 ? '' : 's'}`}
                    />
                  )}
                  <Chip size="small" variant="outlined" label={session.status} />
                </Box>
              </ListItem>
            </Box>
          ))}
        </List>
      )}
      {nextCursor && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button onClick={handleLoadMore} disabled={isFetching}>
            {isFetching ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
      {loadMoreFailed && (
        <Typography color="error" sx={{ px: 2, pb: 2, textAlign: 'center' }}>
          Failed to load more Sessions.
        </Typography>
      )}
    </Paper>
  );
}
